import { WorkflowMetrics, WorkflowActivitiesList, WorkflowTriggerSection, WorkflowConfigSection } from "./ui";
import { SidePanelContainer, SidePanelHeader } from "../ui";
import { Divider } from "../../primitives";
import type {
  WorkflowMetrics as WorkflowMetricsType,
  WorkflowDisplayTriggerRule,
} from "@acto/taskpane/core/data-access/workflow";
import type { Workflow, WorkflowActivity } from "@acto/taskpane/core/domain";
import { useTranslations } from "@acto/web-common/locales";
import { useAvailableFilters } from "@acto/taskpane/core/data-access/workflow";
import { formatDate } from "../../../utils/dateFormatters";

interface WorkflowDetailsSidePanelProps {
  workflow: Workflow;
  metrics: WorkflowMetricsType | null;
  activities: WorkflowActivity[];
  locale: string;
  onEdit?: () => void;
}

export const filterQueryToDisplayRules = (query: Workflow["query"], locale: string): WorkflowDisplayTriggerRule[] => {
  const rules: WorkflowDisplayTriggerRule[] = [];
  for (const group of query.groups) {
    for (const condition of group.conditions) {
      const valueParam = condition.params.find((p) => p.name === "value");
      const timeframeParam = condition.params.find((p) => p.name === "timeframe");
      const value =
        valueParam?.stringValues?.join(", ") ??
        valueParam?.stringValue ??
        valueParam?.numericValue?.toString() ??
        valueParam?.intValue?.toString() ??
        (valueParam?.dateValue ? formatDate(valueParam.dateValue, locale) : undefined) ??
        valueParam?.boolValue?.toString() ??
        "";
      const operator = valueParam?.operator ?? "";
      rules.push({
        field: condition.filter,
        operator,
        value,
        timeframe: timeframeParam?.stringValue ?? undefined,
      });
    }
  }
  return rules;
};

export const WorkflowDetailsSidePanel = ({
  workflow,
  metrics,
  activities,
  locale,
  onEdit,
}: WorkflowDetailsSidePanelProps) => {
  const t = useTranslations("managementView.workflowDetails");
  const { filters } = useAvailableFilters();

  const triggerRules = filterQueryToDisplayRules(workflow.query, locale);

  return (
    <SidePanelContainer>
      <SidePanelHeader title={workflow.name} subtitle={`${workflow.activeTodosCount} ${t("activities")}`} />

      {metrics && <WorkflowMetrics metrics={metrics} />}

      <Divider />

      <WorkflowTriggerSection
        triggerRules={triggerRules}
        conjunction={workflow.query.groupConjunction}
        availableFilters={filters}
        onEdit={workflow.isCustomQuery ? undefined : onEdit}
        isCustomQuery={workflow.isCustomQuery}
      />

      <WorkflowConfigSection
        repeat={workflow.repeat}
        dueDate={workflow.dueDate}
        pauseTill={workflow.pauseTill}
        responsibleEmployeeName={workflow.fixedResponsibleEmployee?.name}
      />

      <Divider />

      <WorkflowActivitiesList activities={activities} locale={locale} />
    </SidePanelContainer>
  );
};
